import { ApolloClient } from '@apollo/client';
import {
  CatalogsQuery,
  CatalogsQueryVariables,
} from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import { DisplayFilters, getDisplayFilters } from './display-filters';
import { GET_CATALOGS } from 'src/graphql-operations/catalog-service/catalogs.query';
import { ClientNames } from 'src/utils/apolloClient';
import { TagCategory } from 'src/services/panacea/types';
import { ClientSafeTenantConfig } from 'server/config-cache/types';
import { getPreSelectedFilters, PlainFilters } from './pre-selected-filters';
import { axiosClient } from 'src/utils/axiosClient';
import { getIngredientsToAvoid } from 'src/services/ecomm-catalog/ingredients-to-avoid';

type MenuPageProps = {
  catalogs: CatalogsQuery['catalogs'];
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
};

/**
 * This function reaches out to various services to gather data that's used for displaying and
 * filtering of menus.
 *
 * With this data it also builds out what filters can be displayed with their default values.
 */
export const initializeMenusAndFilters = async (input: {
  apolloClient: ApolloClient;
  tenantId: number;
  tenantEligibilityLogicalId: string;
  zipCode: string;
  tenantConfig: ClientSafeTenantConfig;
  plainFiltersList?: PlainFilters[];
  tenantPageFilter?: 'customizer' | 'funnel';
}): Promise<MenuPageProps> => {
  const {
    apolloClient,
    tenantId,
    tenantEligibilityLogicalId,
    zipCode,
    tenantConfig,
    plainFiltersList,
    tenantPageFilter,
  } = input;

  const [catalogs, tagCategories, ingredientsToAvoid] = await Promise.all([
    getCatalogs({
      apolloClient,
      tenantId,
      tenantEligibilityLogicalId,
      zipCode,
    }),
    getTagCategories(),
    getIngredientsToAvoid({
      apolloClient,
    }),
  ]);

  const displayFilters = getDisplayFilters({
    catalogs,
    ingredientsToAvoid,
    tagCategories,
    tenantConfig,
    tenantPageFilter,
  });

  const selectedFilters = plainFiltersList
    ? getPreSelectedFilters({
        plainFiltersList,
        displayFilters,
      })
    : {};

  return {
    catalogs,
    displayFilters,
    selectedFilters,
  };
};

const getCatalogs = async (input: {
  apolloClient: ApolloClient;
  zipCode: string;
  tenantId: number;
  tenantEligibilityLogicalId: string;
}): Promise<CatalogsQuery['catalogs']> => {
  const { apolloClient, zipCode, tenantId } = input;

  const { data } = await apolloClient.query<
    CatalogsQuery,
    CatalogsQueryVariables
  >({
    query: GET_CATALOGS,
    variables: {
      catalogsInput: {
        filter: {
          zipCodes: [zipCode],
          tenantIds: [tenantId],
          tenantEligibilityConfigLogicalId: input.tenantEligibilityLogicalId,
          sellable: true,
        },
      },
      itemsInput: { filter: {} },
    },
    context: { clientName: ClientNames.CATALOG_SERVICE_GRAPH },
    fetchPolicy: 'no-cache',
  });

  return data?.catalogs ?? [];
};

const getTagCategories = async (): Promise<TagCategory[]> => {
  const response = await axiosClient.get(
    '/api/v1/ecomm-catalog-rest/tags/tag-categories',
  );
  return response.data;
};
